import type { ReactNode } from 'react';
import { Blocks, RefreshCw, ShieldCheck } from '@vitness/icons';
import { useTheme } from '@vitness/theme';
import { LogoMark, patternImage, PATTERN_TILE, PATTERN_DEFAULT } from '@vitness/ui';

export interface AuthPageProps {
  /** Clerk's SignIn/SignUp component, or another authentication provider UI. */
  children: ReactNode;
  /** Product name shown beside the Vitness brand. */
  applicationName?: string;
  /** Accessible name for the authentication provider region. */
  authLabel?: string;
  /** Optional replacement for the default brand headline. */
  headline?: ReactNode;
  /** Optional context label shown above the brand headline. */
  eyebrow?: ReactNode;
  /** Optional replacement for the supporting brand description. */
  description?: ReactNode;
  /** Optional content rendered beneath the authentication provider. */
  footer?: ReactNode;
  /** Destination used by the brand link. */
  homeHref?: string;
  /**
   * Wordmark text. Defaults to the Vitness brand.
   *
   * LOCAL ADDITION (not upstream). Vudget is its own product rather than a
   * Vitness surface, so it needs its own name in the lockup — but the default
   * keeps every other consumer rendering exactly as before.
   */
  brandName?: string;
  /** Mark shown in the accent tile. Defaults to the Vitness LogoMark. */
  brandIcon?: ReactNode;
}

const RESPONSIVE_STYLES = `
  .vn-auth-page {
    min-height: 100vh;
    min-height: 100dvh;
  }

  .vn-auth-shell {
    min-height: 100vh;
    min-height: 100dvh;
    display: grid;
    grid-template-columns: minmax(0, 1.12fr) minmax(420px, 0.88fr);
  }

  .vn-auth-brand {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    min-width: 0;
    padding: clamp(32px, 5vw, 72px);
  }

  .vn-auth-provider-column {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 32px;
  }

  .vn-auth-provider-card {
    width: min(100%, 440px);
  }

  .vn-auth-mobile-context {
    display: none;
  }

  @container (min-width: 0px) {
    .vn-auth-page,
    .vn-auth-shell {
      min-height: 100cqh;
    }
  }

  @media (max-width: 900px) {
    .vn-auth-shell {
      grid-template-columns: 1fr;
    }

    .vn-auth-brand {
      display: none;
    }

    .vn-auth-provider-column {
      min-height: 100vh;
      min-height: 100dvh;
      min-height: 100cqh;
      align-items: flex-start;
      border-left: 0 !important;
      padding: 28px 20px;
    }

    .vn-auth-mobile-context {
      display: block;
    }
  }

  @container (max-width: 900px) {
    .vn-auth-shell {
      grid-template-columns: 1fr;
    }

    .vn-auth-brand {
      display: none;
    }

    .vn-auth-provider-column {
      min-height: 100vh;
      min-height: 100dvh;
      min-height: 100cqh;
      align-items: flex-start;
      border-left: 0 !important;
      padding: 28px 20px;
    }

    .vn-auth-mobile-context {
      display: block;
    }
  }

  @media (max-width: 480px) {
    .vn-auth-provider-column {
      padding: 20px 14px;
    }
  }

  @container (max-width: 480px) {
    .vn-auth-provider-column {
      padding: 20px 14px;
    }
  }
`;

const DEFAULT_HEADLINE = (
  <>
    One identity.
    <br />
    Every ambition.
  </>
);

const DEFAULT_DESCRIPTION = (
  <>
    Your progress belongs to you. Move between Vitness applications without
    losing the history, context, or momentum you have built.
  </>
);

/**
 * Shared Vitness authentication shell. The component owns the brand experience
 * while authentication behavior remains supplied by Clerk or another provider.
 */
export function AuthPage({
  children,
  applicationName = 'Platform',
  authLabel = 'Authentication',
  headline = DEFAULT_HEADLINE,
  eyebrow = 'Your Vitness identity',
  description = DEFAULT_DESCRIPTION,
  footer,
  homeHref = '/',
  brandName = 'vitness',
  brandIcon,
}: AuthPageProps) {
  const { accent, accentSafe, accentRgb, mode } = useTheme();
  const rgb = `${accentRgb.r},${accentRgb.g},${accentRgb.b}`;
  const isLight = mode === 'light';
  const gridDot = isLight ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.09)';

  return (
    <main
      className="vn-auth-page"
      style={{
        position: 'relative',
        overflow: 'hidden',
        color: 'var(--vn-text-primary)',
        background: 'var(--vn-surface-bg)',
      }}
    >
      <style>{RESPONSIVE_STYLES}</style>

      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          backgroundImage: patternImage(gridDot, PATTERN_DEFAULT),
          backgroundSize: `${PATTERN_TILE}px ${PATTERN_TILE}px`,
          WebkitMaskImage: 'linear-gradient(90deg, black, transparent 72%)',
          maskImage: 'linear-gradient(90deg, black, transparent 72%)',
          opacity: 0.75,
        }}
      />
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          width: 'min(70vw, 900px)',
          height: 'min(70vw, 900px)',
          left: '-24vw',
          top: '10vh',
          borderRadius: '50%',
          pointerEvents: 'none',
          background: `radial-gradient(circle, rgba(${rgb},${isLight ? 0.09 : 0.11}), transparent 67%)`,
          filter: 'blur(12px)',
        }}
      />

      <div className="vn-auth-shell" style={{ position: 'relative', zIndex: 1 }}>
        <section className="vn-auth-brand" aria-labelledby="vn-auth-brand-heading">
          <BrandLockup
            applicationName={applicationName}
            homeHref={homeHref}
            accent={accent}
            brandName={brandName}
            brandIcon={brandIcon}
          />

          <div style={{ maxWidth: 660, padding: '72px 0' }}>
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                marginBottom: 24,
                fontFamily: 'var(--vn-font-mono)',
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: accentSafe,
              }}
            >
              <span
                aria-hidden="true"
                style={{ width: 24, height: 1, background: accentSafe }}
              />
              {eyebrow}
            </div>
            <h1
              id="vn-auth-brand-heading"
              style={{
                margin: 0,
                maxWidth: 620,
                fontFamily: 'var(--vn-font-display)',
                fontSize: 'clamp(44px, 6.2vw, 82px)',
                fontWeight: 800,
                lineHeight: 0.98,
                letterSpacing: '-0.055em',
              }}
            >
              {headline}
            </h1>
            <p
              style={{
                maxWidth: 560,
                margin: '28px 0 0',
                fontSize: 'clamp(14px, 1.3vw, 17px)',
                lineHeight: 1.75,
                color: 'var(--vn-text-secondary)',
              }}
            >
              {description}
            </p>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
              gap: 12,
              maxWidth: 700,
            }}
          >
            <TrustSignal icon={<RefreshCw size={15} />} title="Portable identity">
              Your account follows you.
            </TrustSignal>
            <TrustSignal icon={<ShieldCheck size={15} />} title="Private by design">
              Secure access by Clerk.
            </TrustSignal>
            <TrustSignal icon={<Blocks size={15} />} title="Ready everywhere">
              One face across every app.
            </TrustSignal>
          </div>
        </section>

        <section
          className="vn-auth-provider-column"
          aria-label={authLabel}
          role="region"
          style={{
            position: 'relative',
            borderLeft: '1px solid var(--vn-border-default)',
            background: isLight ? 'rgba(255,255,255,0.58)' : 'rgba(12,12,15,0.72)',
            backdropFilter: 'blur(18px)',
          }}
        >
          <div className="vn-auth-provider-card">
            <div className="vn-auth-mobile-context" style={{ marginBottom: 34 }}>
              <BrandLockup
                applicationName={applicationName}
                homeHref={homeHref}
                accent={accent}
                brandName={brandName}
                brandIcon={brandIcon}
              />
              <p
                style={{
                  margin: '26px 0 0',
                  color: 'var(--vn-text-secondary)',
                  fontSize: 13,
                  lineHeight: 1.6,
                }}
              >
                One secure account across the Vitness ecosystem.
              </p>
            </div>

            <div
              style={{
                border: '1px solid var(--vn-border-default)',
                borderRadius: 14,
                background: 'var(--vn-surface-card)',
                boxShadow: isLight
                  ? '0 24px 80px rgba(25,25,35,0.10)'
                  : '0 30px 100px rgba(0,0,0,0.34)',
              }}
            >
              <div
                aria-hidden="true"
                style={{
                  height: 2,
                  // Rounded to match the card so the card itself needs no
                  // `overflow: hidden` (which was clipping the inner form's
                  // left border).
                  borderRadius: '13px 13px 0 0',
                  background: `linear-gradient(90deg, ${accent}, rgba(${rgb},0.16), transparent)`,
                }}
              />
              <div style={{ padding: 'clamp(22px, 4vw, 34px)' }}>{children}</div>
            </div>

            {footer && (
              <div
                style={{
                  marginTop: 18,
                  textAlign: 'center',
                  color: 'var(--vn-text-muted)',
                  fontSize: 11,
                  lineHeight: 1.6,
                }}
              >
                {footer}
              </div>
            )}
          </div>
        </section>
      </div>
    </main>
  );
}

function BrandLockup({
  applicationName,
  homeHref,
  accent,
  brandName,
  brandIcon,
}: {
  applicationName: string;
  homeHref: string;
  accent: string;
  brandName: string;
  brandIcon?: ReactNode;
}) {
  return (
    <a
      href={homeHref}
      aria-label={`${brandName} ${applicationName} home`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 12,
        width: 'fit-content',
        color: 'var(--vn-text-primary)',
        textDecoration: 'none',
      }}
    >
      <span
        aria-hidden="true"
        style={{
          display: 'grid',
          width: 36,
          height: 36,
          placeItems: 'center',
          borderRadius: 9,
          color: '#09090B',
          background: accent,
          boxShadow: `0 0 30px color-mix(in srgb, ${accent} 16%, transparent)`,
        }}
      >
        {brandIcon ?? <LogoMark size={22} />}
      </span>
      <span style={{ display: 'flex', alignItems: 'baseline', gap: 7 }}>
        <span
          style={{
            fontFamily: 'var(--vn-font-display)',
            fontSize: 18,
            fontWeight: 800,
            letterSpacing: '-0.025em',
          }}
        >
          {brandName}
        </span>
        {applicationName && (
        <span
          aria-hidden="true"
          style={{ width: 1, height: 13, background: 'var(--vn-border-strong)' }}
        />
        )}
        {applicationName && (
        <span
          style={{
            fontFamily: 'var(--vn-font-mono)',
            fontSize: 9,
            fontWeight: 600,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: 'var(--vn-text-muted)',
          }}
        >
          {applicationName}
        </span>
        )}
      </span>
    </a>
  );
}

function TrustSignal({
  icon,
  title,
  children,
}: {
  icon: ReactNode;
  title: string;
  children: ReactNode;
}) {
  const { accentSafe } = useTheme();

  return (
    <div
      style={{
        minWidth: 0,
        paddingTop: 14,
        borderTop: '1px solid var(--vn-border-default)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 7,
          color: accentSafe,
        }}
      >
        {icon}
        <strong
          style={{
            overflow: 'hidden',
            fontSize: 11,
            fontWeight: 700,
            color: 'var(--vn-text-primary)',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {title}
        </strong>
      </div>
      <div
        style={{
          marginTop: 5,
          fontSize: 10,
          lineHeight: 1.5,
          color: 'var(--vn-text-muted)',
        }}
      >
        {children}
      </div>
    </div>
  );
}
